import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, catchError, concatMap, debounceTime, expand, filter, firstValueFrom, from, last, map, of, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { PatientNavigationLock } from '../utils/patient-navigation-lock';
import { formatPatientDates, splitPatientLists } from '../utils/patient-lists';
import { SavedCopy, SavedPatientList, openSavedCopy } from '../utils/patient-list-saved-copy';
import { hashIds, listRow, mergeChanges } from '../utils/patient-list-sync';

/** Server-side state of a queued full-patient-file export. */
export interface PatientFileExportStatus {
  exportId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string | null;
  subMessage: string | null;
  fileName: string | null;
  fileSize: number | null;
  errorMessage: string | null;
  expiresAt: string | null;
  /** Only present on the "what's already there" lookup. */
  createdAt?: string;
  /**
   * What this export was built to contain. Null options on the server (any export queued
   * before the feature) normalise to every section and no audio, so this is always populated.
   */
  options?: { sections: { [key: string]: boolean }; includeAudio: boolean };
}

/** GET /patients/:id/patient-app (tasks/patient-app.md §4.5). `available` false: the practice has not opted in. */
export interface PatientAppLink {
  available: boolean;
  linked: boolean;
  linkedAt: string | null;
  canEnd: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private apiUrl = 'api';
  private patientsSubject = new BehaviorSubject<any[]>([]);
  private recentPatientsSubject = new BehaviorSubject<any[]>([]);
  private inpatientsSubject = new BehaviorSubject<any[]>([]);
  private outpatientsSubject = new BehaviorSubject<any[]>([]);
  private invitesSubject = new BehaviorSubject<any[]>([]);
  private archivesSubject = new BehaviorSubject<any[]>([]);
  private deceasedSubject = new BehaviorSubject<any[]>([]);
  private alertsSubject = new BehaviorSubject<any[]>([]);
  private currentPatientSubject = new BehaviorSubject<any>(null);
  private patientChangedSubject = new BehaviorSubject<boolean>(false);

  patients$ = this.patientsSubject.asObservable();
  recentPatients$ = this.recentPatientsSubject.asObservable();
  inpatients$ = this.inpatientsSubject.asObservable();
  outpatients$ = this.outpatientsSubject.asObservable();
  invites$ = this.invitesSubject.asObservable();
  archives$ = this.archivesSubject.asObservable();
  /** Read-only projection of Patient.isDeceased — derived from mortality notes server-side, never set from the client. */
  deceased$ = this.deceasedSubject.asObservable();
  alerts$ = this.alertsSubject.asObservable();
  currentPatient$ = this.currentPatientSubject.asObservable();

  lockRecordingToPatient = new BehaviorSubject<boolean>(false);
  /** Holders that must block switching patients: the floating AI dialogs, and screens mid-save for this patient. */
  private readonly patientNavigationLock = new PatientNavigationLock();
  forceAiDialogRestore = new BehaviorSubject<boolean>(false);
  private aiDialogOpenSubject = new BehaviorSubject<boolean>(false);
  public aiDialogOpen$ = this.aiDialogOpenSubject.asObservable();

  groupId: any;
  currentPage = 1;
  pageSize = 500;
  isLoading = false;
  /** Bump when the list row fields change, so saved copies of the old shape are thrown away. */
  readonly listVersion = 1;
  /** The open saved copy of the selected practice's list and the server time it is in sync with. */
  private savedCopy: SavedCopy | null = null;
  private syncedAt: string | null = null;
  private fullAt: string | null = null;
  private lastSaved: any[] | null = null;
  /** Replaced in specs. */
  openCopy: (key: string, version: string, groupId: string) => Promise<SavedCopy> = openSavedCopy;
  triggerAiRecording = new BehaviorSubject<boolean>(false);

  // Methods to track AI dialog open state
  setAiDialogOpen(isOpen: boolean): void {
    this.aiDialogOpenSubject.next(isOpen);
  }

  isAiDialogOpen(): boolean {
    return this.aiDialogOpenSubject.value;
  }

  /** Holds the current patient in place. `reason` is what the user is told when they try to leave (default: the AI dialog). */
  acquirePatientNavigationLock(reason?: string): void {
    this.patientNavigationLock.acquire(reason);
  }

  /** Lets go of a lock taken with the same reason. */
  releasePatientNavigationLock(reason?: string): void {
    this.patientNavigationLock.release(reason);
  }

  private isNavigationLocked(): boolean {
    return this.lockRecordingToPatient.value || this.patientNavigationLock.locked;
  }

  /**
   * When AI is active, block clearing the current patient or loading a different patient — not in-file data loads.
   */
  private shouldBlockPatientNavigationInGetPatientById(
    id: string | null,
    dontClearCurrentPatient: boolean,
    currentPatient: any
  ): boolean {
    if (!this.isNavigationLocked()) {
      return false;
    }
    if (id === null && !dontClearCurrentPatient) {
      return true;
    }
    if (id !== null && currentPatient !== null && currentPatient.id !== id) {
      return true;
    }
    return false;
  }

  private showPatientNavigationBlockedMessage(): void {
    const { message, restoreAiDialog } = this.patientNavigationLock.blocked();
    this.snack.open(message, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    if (restoreAiDialog) this.forceAiDialogRestore.next(true);
  }

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService,
    private snack: MatSnackBar,
  ) {

    this.setupRealTimeUpdates();

    // Socket changes land in patients$; keep the saved copy in step once the list is in sync with the server.
    this.patients$.pipe(
      debounceTime(3000),
      filter(patients => !!this.savedCopy && !!this.syncedAt && !this.isLoading && patients !== this.lastSaved),
      concatMap(patients => from(this.saveCopy(patients)))
    ).subscribe();

    // Events sent while the socket was down are missed; fetch what changed since the last sync instead.
    this.socketService.reconnected$.pipe(
      filter(connected => connected && !!this.syncedAt && !this.isLoading),
      switchMap(() => {
        this.isLoading = true;
        return this.syncSince(this.groupId, this.syncedAt!).pipe(tap(() => (this.isLoading = false)));
      })
    ).subscribe();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          return this.loadPatients(true);
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private resetForReload() {
    this.isLoading = false;
    this.currentPage = 1;
    this.savedCopy = null;
    this.syncedAt = null;
    this.fullAt = null;
    this.patientsSubject.next([]);
    this.recentPatientsSubject.next([]);
    this.inpatientsSubject.next([]);
    this.outpatientsSubject.next([]);
    this.invitesSubject.next([]);
    this.archivesSubject.next([]);
    this.deceasedSubject.next([]);
    this.alertsSubject.next([]);
  }

  /**
   * A patient shows on the deceased list only while they are still active — archiving wins over
   * deceased, so an archived patient shows under Archives only.
   */
  private belongsOnDeceasedList(patient: any): boolean {
    return patient?.isDeceased === true && patient?.active !== false;
  }

  private setupRealTimeUpdates(): void {
    // Listen for a new patient being added
    this.socketService.listen('patientAdded', 'patient-service').subscribe((newPatient) => {
      try {
        const formattedPatient = formatPatientDates(newPatient);

        const currentPatients = this.patientsSubject.getValue();
        this.patientsSubject.next([...currentPatients, formattedPatient]); // Add the new patient

        if (newPatient.requiresValidation || newPatient.requiresICDValidation || newPatient.wasSelfBoarded) {
          const currentAlerts = this.alertsSubject.getValue();
          const alertIndex = currentAlerts.findIndex(alert => alert.id === formattedPatient.id);

          if (alertIndex !== -1) {
            // Update existing alert
            currentAlerts[alertIndex] = formattedPatient;
          } else {
            // Add new alert
            currentAlerts.push(formattedPatient);
          }
          this.alertsSubject.next([...currentAlerts]);
        }

        if (this.belongsOnDeceasedList(formattedPatient)) {
          // Deceased patients never enter the working lists — they only show on the deceased list.
          const currentDeceased = this.deceasedSubject.getValue();
          this.deceasedSubject.next([...currentDeceased, formattedPatient]);
        } else {
          switch (formattedPatient.status) {
            case 'inpatient':
              const currentInpatients = this.inpatientsSubject.getValue();
              this.inpatientsSubject.next([...currentInpatients, formattedPatient]);
              break;
            case 'opd':
              const currentOutpatients = this.outpatientsSubject.getValue();
              this.outpatientsSubject.next([...currentOutpatients, formattedPatient]);
              const currentRecent = this.recentPatientsSubject.getValue();
              this.recentPatientsSubject.next([...currentRecent, formattedPatient]);
              break;
            case 'invite':
              const currentInvites = this.invitesSubject.getValue();
              this.invitesSubject.next([...currentInvites, formattedPatient]);
              break;
          }
          // Also add to invite list when requiresValidation (e.g. quick-add) so invites can be resent
          if (formattedPatient.requiresValidation && formattedPatient.status !== 'invite') {
            const currentInvites = this.invitesSubject.getValue();
            if (!currentInvites.some(p => p.id === formattedPatient.id)) {
              this.invitesSubject.next([...currentInvites, formattedPatient]);
            }
          }
        }
      } catch (error) {
        console.error('Error adding socket patient: ', error);
      }

    });

    /**
     * ICD-10 codes changed on a patient — a suggestion accepted or dismissed, or the nightly
     * pipeline auto-applying one (§11).
     *
     * A MERGE, not a replace. The server sends only `{ patientId, icdCodes }` precisely so this
     * cannot blank anything else: `patientUpdated` carries a whole patient and replaces the
     * subject wholesale, which would need the server to rebuild the full include list from
     * `GetPatientById` just to change one array. Narrow event, narrow update.
     *
     * Accepting a suggestion soft-deletes the code being removed and creates the new one; the
     * server re-reads the ACTIVE list, so a removed code simply disappears from this array.
     */
    this.socketService.listen('patientIcdCodesUpdated', 'patient-service').subscribe((payload: any) => {
      if (!payload?.patientId || !Array.isArray(payload.icdCodes)) return;

      const applyCodes = (p: any) => (p && p.id === payload.patientId
        ? { ...p, icdCodes: payload.icdCodes }
        : p);

      const current = this.currentPatientSubject.getValue();
      if (current?.id === payload.patientId) {
        this.currentPatientSubject.next(applyCodes(current));
      }

      // The lists carry the same patient objects, so a chip rendered from a list view would
      // otherwise stay stale until the next fetch.
      this.patientsSubject.next(this.patientsSubject.getValue().map(applyCodes));
      this.recentPatientsSubject.next(this.recentPatientsSubject.getValue().map(applyCodes));
    });

    // Listen for patient updates (this covers both restoration and regular updates)
    this.socketService.listen('patientUpdated', 'patient-service').subscribe((updatedPatient) => {
      const formattedPatient = formatPatientDates(updatedPatient);

      if (updatedPatient.requiresValidation === false) {
        this.groupService.adjustPatientValidationStatus$.next(updatedPatient.id);
      }

      // 1. Update the main patients list: update if exists, or add if new.
      const patients = this.patientsSubject.getValue();
      const patientIndex = patients.findIndex(p => p.id === formattedPatient.id);
      if (patientIndex !== -1) {
        patients[patientIndex] = formattedPatient;
      } else {
        patients.push(formattedPatient);
      }
      this.patientsSubject.next([...patients]);

      // Handle alerts with proper duplication prevention
      const currentAlerts = this.alertsSubject.getValue();
      const alertIndex = currentAlerts.findIndex(p => p.id === formattedPatient.id);

      if (formattedPatient.requiresValidation || formattedPatient.requiresICDValidation || formattedPatient.wasSelfBoarded) {
        if (alertIndex !== -1) {
          // Update existing alert
          currentAlerts[alertIndex] = formattedPatient;
        } else {
          // Add new alert
          currentAlerts.push(formattedPatient);
        }
        this.alertsSubject.next([...currentAlerts]);
      } else {
        // Remove from alerts if no longer requires validation
        if (alertIndex !== -1) {
          currentAlerts.splice(alertIndex, 1);
          this.alertsSubject.next([...currentAlerts]);
        }
      }

      // 2. If this is the current patient, update the current patient subject.
      if (formattedPatient.id === this.currentPatientSubject.getValue()?.id) {
        this.currentPatientSubject.next(formattedPatient);
      }

      // isDeceased is derived server-side from the patient's mortality notes and re-emitted on
      // every note change, so this is the whole live-update path for the deceased list.
      const onDeceasedList = this.belongsOnDeceasedList(formattedPatient);

      // 3. Update the recent patients list similarly.
      const recentPatients = this.recentPatientsSubject.getValue();
      const recentIndex = recentPatients.findIndex(p => p.id === formattedPatient.id);
      if (onDeceasedList) {
        if (recentIndex !== -1) recentPatients.splice(recentIndex, 1);
      } else if (recentIndex !== -1) {
        recentPatients[recentIndex] = formattedPatient;
      } else {
        recentPatients.push(formattedPatient);
      }
      this.recentPatientsSubject.next([...recentPatients]);

      // 4. Update status-specific lists (inpatients, outpatients, invites, deceased)
      const inpatients = this.inpatientsSubject.getValue();
      const outpatients = this.outpatientsSubject.getValue();
      const invites = this.invitesSubject.getValue();
      const deceased = this.deceasedSubject.getValue();

      const inpatientIndex = inpatients.findIndex(p => p.id === formattedPatient.id);
      const outpatientIndex = outpatients.findIndex(p => p.id === formattedPatient.id);
      const inviteIndex = invites.findIndex(p => p.id === formattedPatient.id);
      const deceasedIndex = deceased.findIndex(p => p.id === formattedPatient.id);

      // Invite list shows patients with status 'invite' OR requiresValidation (e.g. quick-add)
      const onInviteList = formattedPatient.status === 'invite' || formattedPatient.requiresValidation === true;

      // No longer deceased (mortality note edited/deleted) or now archived: drop off the deceased list.
      if (!onDeceasedList && deceasedIndex !== -1) {
        deceased.splice(deceasedIndex, 1);
      }

      if (onDeceasedList) {
        // Deceased takes precedence over the working lists: pull them out of all three, then
        // add or update in place on the deceased list.
        if (inpatientIndex !== -1) inpatients.splice(inpatientIndex, 1);
        if (outpatientIndex !== -1) outpatients.splice(outpatientIndex, 1);
        if (inviteIndex !== -1) invites.splice(inviteIndex, 1);
        if (deceasedIndex !== -1) {
          deceased[deceasedIndex] = formattedPatient;
        } else {
          deceased.push(formattedPatient);
        }
      } else if (onInviteList) {
        // Remove from inpatients and outpatients then add/update in invites.
        if (inpatientIndex !== -1) inpatients.splice(inpatientIndex, 1);
        if (outpatientIndex !== -1) outpatients.splice(outpatientIndex, 1);
        if (inviteIndex !== -1) {
          invites[inviteIndex] = formattedPatient;
        } else {
          invites.push(formattedPatient);
        }
      } else {
        // No longer on invite list (e.g. completed invite): remove from invites if present.
        if (inviteIndex !== -1) invites.splice(inviteIndex, 1);
        // Handle status-specific lists
        if (formattedPatient.status === 'inpatient') {
          if (outpatientIndex !== -1) outpatients.splice(outpatientIndex, 1);
          if (inpatientIndex !== -1) {
            inpatients[inpatientIndex] = formattedPatient;
          } else {
            inpatients.push(formattedPatient);
          }
        } else if (formattedPatient.status === 'opd') {
          if (inpatientIndex !== -1) inpatients.splice(inpatientIndex, 1);
          if (outpatientIndex !== -1) {
            outpatients[outpatientIndex] = formattedPatient;
          } else {
            outpatients.push(formattedPatient);
          }
        }
      }

      // 5. If the patient is active (e.g. has been restored), remove them from the archives.
      if (formattedPatient.active) {
        const archivePatients = this.archivesSubject.getValue();
        const archiveIndex = archivePatients.findIndex(p => p.id === formattedPatient.id);
        if (archiveIndex !== -1) {
          archivePatients.splice(archiveIndex, 1);
          this.archivesSubject.next([...archivePatients]);
        }
      }

      // 6. Finally, notify the status-specific subjects of the updates.
      this.inpatientsSubject.next([...inpatients]);
      this.outpatientsSubject.next([...outpatients]);
      this.invitesSubject.next([...invites]);
      this.deceasedSubject.next([...deceased]);
    });


    // Listen for a patient being deleted
    this.socketService.listen('patientDeleted', 'patient-service').subscribe((deletedPatient) => {
      const formattedPatient = formatPatientDates(deletedPatient);

      const currentPatients = this.patientsSubject.getValue();
      const updatedPatients = currentPatients.filter(p => p.id !== formattedPatient.id);
      this.patientsSubject.next(updatedPatients); // Remove the deleted patient

      // Optionally update other subjects if needed
      const currentInpatients = this.inpatientsSubject.getValue();
      const updatedInpatients = currentInpatients.filter(p => p.id !== formattedPatient.id);
      this.inpatientsSubject.next(updatedInpatients);

      const currentOutpatients = this.outpatientsSubject.getValue();
      const updatedOutpatients = currentOutpatients.filter(p => p.id !== formattedPatient.id);
      this.outpatientsSubject.next(updatedOutpatients);

      const currentInvites = this.invitesSubject.getValue();
      const updatedInvites = currentInvites.filter(p => p.id !== formattedPatient.id);
      this.invitesSubject.next(updatedInvites);

      // Archiving wins over deceased — the patient moves to Archives below, so drop them here.
      const currentDeceased = this.deceasedSubject.getValue();
      const updatedDeceased = currentDeceased.filter(p => p.id !== formattedPatient.id);
      this.deceasedSubject.next(updatedDeceased);

      const currentRecent = this.recentPatientsSubject.getValue();
      const updatedRecent = currentRecent.filter(p => p.id !== formattedPatient.id);
      this.recentPatientsSubject.next(updatedRecent);

      const currentArchive = this.archivesSubject.getValue();
      if (!currentArchive.some(p => p.id === formattedPatient.id)) {
        const updatedArchive = [...currentArchive, formattedPatient];
        this.archivesSubject.next(updatedArchive);
      }

      const currentAlerts = this.alertsSubject.getValue();
      const updatedAlerts = currentAlerts.filter(p => p.id !== formattedPatient.id);
      this.alertsSubject.next(updatedAlerts);
    });
  }

  get patientChanged(): boolean {
    return this.patientChangedSubject.getValue();
  }

  /**
   * Loads the selected practice's patient list. With a saved copy on this device it is shown at once and only the
   * patients changed since it was saved are fetched; without one every page is loaded, as before, and then saved.
   */
  loadPatients(reload: boolean = false): Observable<any[]> {
    if (reload) {
      this.resetForReload();
    }
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    const groupId = this.groupId;
    return from(this.openSavedList(groupId)).pipe(
      switchMap(saved => saved && !this.fullLoadDue(saved) ? this.syncSavedList(groupId, saved) : this.loadAllPages(groupId)),
      tap(() => (this.isLoading = false)),
      catchError(() => {
        this.isLoading = false;
        return of(this.patientsSubject.getValue());
      })
    );
  }

  private async openSavedList(groupId: string): Promise<SavedPatientList | undefined> {
    try {
      const res: any = await firstValueFrom(this.http.get(`${this.apiUrl}/patients/group/${groupId}/cache-key`, this.authHeaders()));
      this.savedCopy = await this.openCopy(res.key, `${this.listVersion}.${res.version}`, groupId);
      return await this.savedCopy.get();
    } catch {
      // No key (not configured, offline) or no IndexedDB: load from the server without a saved copy.
      this.savedCopy = null;
      return undefined;
    }
  }

  /**
   * A full load at least once a week, so a change the changes-only load cannot see (a direct database fix that leaves
   * updatedAt alone) does not stay on the device for long.
   */
  private fullLoadDue(saved: SavedPatientList): boolean {
    return !saved.fullAt || Date.now() - Date.parse(saved.fullAt) > 7 * 24 * 3600_000;
  }

  /** Every page, shown as it arrives; then saved with the server time of the first page. */
  private loadAllPages(groupId: string): Observable<any[]> {
    let rows: any[] = [];
    let syncedAt: string | null = null;
    const fullAt = new Date().toISOString();
    this.currentPage = 1;
    return this.fetchPages(groupId).pipe(
      filter(() => groupId === this.groupId),
      tap((res: any) => {
        syncedAt ??= res.syncedAt ?? null;
        // Only the new page is formatted; earlier pages were formatted when they arrived.
        rows = [...rows, ...res.data.map(formatPatientDates)];
        this.processPatients(rows);
      }),
      last(),
      concatMap(() => {
        this.syncedAt = syncedAt;
        this.fullAt = fullAt;
        return from(this.saveCopy(rows)).pipe(map(() => rows));
      })
    );
  }

  /** Shows the saved copy, then brings it up to date. */
  private syncSavedList(groupId: string, saved: SavedPatientList): Observable<any[]> {
    this.processPatients(saved.rows);
    this.fullAt = saved.fullAt;
    return this.syncSince(groupId, saved.syncedAt);
  }

  private syncSince(groupId: string, since: string): Observable<any[]> {
    let changed: any[] = [];
    let first: any = null;
    return this.fetchPages(groupId, since).pipe(
      tap((res: any) => {
        first ??= res;
        changed = [...changed, ...res.data.map(formatPatientDates)];
      }),
      last(),
      concatMap(() => from(this.applyChanges(groupId, changed, first))),
      catchError(() => {
        // What is on screen stays; the next login, group switch or reconnect tries again.
        return of(this.patientsSubject.getValue());
      })
    );
  }

  private async applyChanges(groupId: string, changed: any[], first: any): Promise<any[]> {
    // Another practice was selected meanwhile: these changes are not for the list on screen.
    if (groupId !== this.groupId) return [];
    // Merged into what is on screen, so socket changes that arrived meanwhile are kept.
    let rows = mergeChanges(this.patientsSubject.getValue(), changed);
    if (first.idsHash && await hashIds(rows.map(p => p.id)) !== first.idsHash) {
      const res: any = await firstValueFrom(this.http.get(`${this.apiUrl}/patients/group/${groupId}?view=ids`, this.authHeaders()));
      const ids = new Set<string>(res.data);
      const local = new Set(rows.map(p => p.id));
      if (res.data.some((id: string) => !local.has(id))) {
        // Patients the copy never saw with no recent change (moved in, restored): start over.
        this.patientsSubject.next([]);
        this.alertsSubject.next([]);
        return firstValueFrom(this.loadAllPages(groupId));
      }
      rows = rows.filter(p => ids.has(p.id));
      // Rebuilt from the rows, so a removed patient's alert goes too.
      this.alertsSubject.next([]);
    }
    this.processPatients(rows);
    this.syncedAt = first.syncedAt ?? null;
    await this.saveCopy(rows);
    return rows;
  }

  private async saveCopy(patients: any[]): Promise<void> {
    if (!this.savedCopy || !this.syncedAt || !this.fullAt) return;
    this.lastSaved = patients;
    try {
      await this.savedCopy.put({ syncedAt: this.syncedAt, fullAt: this.fullAt, rows: patients.map(listRow) });
    } catch {
      // Storage full or unavailable: the list still works from memory.
    }
  }

  /** Page 1, 2, ... until a short page. With `since`, only the patients changed after it. */
  private fetchPages(groupId: string, since?: string): Observable<any> {
    const page = (n: number) => this.getPatientsByGroup(groupId, n, this.pageSize, since);
    return page(1).pipe(
      expand((res: any, index: number) => res.data.length === this.pageSize ? page(index + 2) : EMPTY)
    );
  }

  getPatientsByGroup(groupId: string, page: number, pageSize: number, since?: string): Observable<any[]> {
    // `since` is the server's own toISOString(), safe in a query string as it is.
    const changesOnly = since ? `&since=${since}` : '';
    return this.http.get<any[]>(`${this.apiUrl}/patients/group/${groupId}?page=${page}&pageSize=${pageSize}&view=list${changesOnly}`, this.authHeaders());
  }

  private authHeaders() {
    return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  }

  processPatients(patients: any[]) {
    const lists = splitPatientLists(patients, this.alertsSubject.getValue());

    this.patientsSubject.next(patients);
    if (this.recentPatientsSubject.getValue().length === 0) {
      this.recentPatientsSubject.next(lists.recentPatients);
    }
    this.inpatientsSubject.next(lists.inpatients);
    this.outpatientsSubject.next(lists.outpatients);
    this.invitesSubject.next(lists.invites);
    this.archivesSubject.next(lists.archives);
    this.deceasedSubject.next(lists.deceased);
    this.alertsSubject.next(lists.alerts);
  }

  findSimilarPatients(patientData: any, threshold: number = 0.65): Observable<any[]> {
    const similarPatients: any[] = [];
    const isInvite = patientData?.status === 'invite';

    // When adding an invite, compare against all active patients (including other invites) to catch duplicates
    const currentPatients = this.patientsSubject.getValue().filter(p =>
      p.active === true && (isInvite ? true : p.status !== 'invite')
    );

    const inviteThreshold = 0.75;

    currentPatients.forEach(patient => {
      let similarityScore = 0;
      let criteriaCount = 0;
      const effectiveThreshold = isInvite ? inviteThreshold : threshold;

      if (isInvite) {
        // Reduced comparison for invite: only fields we typically have (name, surname, email, mobile)
        if (patientData.name != null && String(patientData.name).trim() !== '') {
          if (this.compareStrings(patient.name, patientData.name)) {
            similarityScore++;
          }
          criteriaCount++;
        }
        if (patientData.surname != null && String(patientData.surname).trim() !== '') {
          if (this.compareStrings(patient.surname, patientData.surname)) {
            similarityScore++;
          }
          criteriaCount++;
        }
        if (patientData.email != null && String(patientData.email).trim() !== '') {
          if (this.compareStrings(patient.email, patientData.email)) {
            similarityScore += 2; // email match is strong signal
          }
          criteriaCount += 2;
        }
        if (patientData.mobileNumber != null && String(patientData.mobileNumber).trim() !== '') {
          if (this.compareStrings(patient.mobileNumber, patientData.mobileNumber)) {
            similarityScore++;
          }
          criteriaCount++;
        }
      } else {
        // Full comparison for non-invite
        if (this.compareStrings(patient.name, patientData.name)) {
          similarityScore++;
        }
        criteriaCount++;

        if (this.compareStrings(patient.surname, patientData.surname)) {
          similarityScore++;
        }
        criteriaCount++;

        if (patientData.dob) {
          const dob1 = moment(patient.dob);
          const dob2 = moment(patientData.dob, 'DD/MM/YYYY');
          if (dob1.isSame(dob2, 'year') && dob1.isSame(dob2, 'month')) {
            similarityScore++;
          }
          criteriaCount++;
        }

        if (patientData.gender) {
          if (patient.gender === patientData.gender) {
            similarityScore++;
          }
          criteriaCount++;
        }
      }

      // Calculate final similarity as a percentage
      const finalSimilarity = criteriaCount > 0 ? similarityScore / criteriaCount : 0;

      if (finalSimilarity >= effectiveThreshold) {
        similarPatients.push(patient);
      }
    });

    return of(similarPatients);
  }

  private compareStrings(str1: string, str2: string): boolean {
    // Convert strings to lower case for case-insensitive comparison
    str1 = str1?.toLowerCase() || '';
    str2 = str2?.toLowerCase() || '';

    // Create a matrix
    const matrix: number[][] = [];

    // Initialize the first row and column of the matrix
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) == str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
            Math.min(matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1)); // deletion
        }
      }
    }

    // The Levenshtein distance is in the bottom-right corner of the matrix
    const distance = matrix[str2.length][str1.length];

    // Define a threshold for how similar the strings must be
    // This can be a fixed number or a percentage of the length of the strings
    const threshold = Math.max(str1.length, str2.length) / 4; // Current threshold: no more than a quarter of the length
    return distance <= threshold;
  }


  addPatient(patientData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/patients`, patientData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  public isRecordingLocked(): boolean {
    if (this.isNavigationLocked()) {
      this.showPatientNavigationBlockedMessage();
      return true;
    }
    return false;
  }

  /** A file's patient app link (tasks/patient-app.md §4.5), for the "Linked to patient app" mark on the patient file. */
  getPatientAppLink(patientId: string): Observable<PatientAppLink> {
    return this.http.get<PatientAppLink>(`${this.apiUrl}/patients/${patientId}/patient-app`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  }

  /** The practice ends a file's patient app link (admins only; the server checks). */
  endPatientAppLink(patientId: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.apiUrl}/patients/${patientId}/patient-app/end`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  }

  getPatientById(id: string | null, dontClearCurrentPatient: boolean = false): Observable<any> {
    const currentPatient = this.currentPatientSubject.getValue();

    if (id && currentPatient && currentPatient.id === id) {
      this.patientChangedSubject.next(false);
      return of(currentPatient);
    }

    if (this.shouldBlockPatientNavigationInGetPatientById(id, dontClearCurrentPatient, currentPatient)) {
      this.showPatientNavigationBlockedMessage();
      return EMPTY;
    }

    if (!dontClearCurrentPatient) {
      this.currentPatientSubject.next(null);
      this.patientChangedSubject.next(true);
    }

    if (!id) {
      /* this.currentPatientSubject.next(null);
      this.patientChangedSubject.next(true); */
      return of(null)
    }

    const token = localStorage.getItem('token');
    const params = new HttpParams().set('groupId', this.groupId);

    return this.http.get<any>(`${this.apiUrl}/patients/${id}`, { //consent/patients/${id}
      params,
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(result => {
        const patient = formatPatientDates(result.data)
        this.setCurrentPatient(patient);
        return of(patient);
      }),
      catchError(error => {
        console.error('Error fetching patient by ID:', error);
        return of(null);
      })
    );
  }

  updatePatient(id: string, patientData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/patients/${id}`, patientData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  dischargePatient(id: string, patientData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/patients/${id}/discharge`, patientData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deletePatient(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/patients/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  mergeDuplicatePatients(moveFromId: string, moveToId: string): Observable<{ success: boolean; message?: string }> {
    const token = localStorage.getItem('token');
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.apiUrl}/patients/merge-duplicate`,
      { moveFromId, moveToId },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  deleteAssociatedData(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/patients/information/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  seedSamplePatients(count: number) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/patients/seed/${count}`, { groupId: this.groupId }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(res => {
        return this.loadPatients();
      })
    );
  }

  get patient() {
    return this.currentPatientSubject.getValue();
  }

  get activePatient(): string | undefined {
    return this.currentPatientSubject.getValue()?.id;
  }

  getPatientMedicalInfo(schemeCode?: string, planCode?: string, planOptionCode?: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/api/patient/medical-aid/${schemeCode}/${planCode}/${planOptionCode}`).pipe(
      map(response => response || {}),
      catchError(error => {
        console.error("Failed to get patient medical data:", error.message);
        return of(null);
      })
    )
  }

  setCurrentPatient(patient: any) {
    if (patient) {
      if (this.currentPatientSubject.getValue()?.id !== patient?.id) {
        this.patientChangedSubject.next(true);
      } else {
        this.patientChangedSubject.next(false);
      }
    } else {
      this.patientChangedSubject.next(true);
    }

    this.currentPatientSubject.next(patient);
  }

  exportPatientsToExcel(groupId: string): Observable<Blob> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/patients/group/${groupId}/export`, {}, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
  }

  // ── Full patient file export ───────────────────────────────────────────────
  //
  // Queues a zip of the patient's entire file (workbook + all media) on the server and
  // returns an export id. Progress arrives on the `export-progress-<exportId>` socket room;
  // getPatientFileExportStatus is the fallback for a dropped socket. Practice admins only —
  // the server enforces it, the button is merely hidden for everyone else.

  /**
   * The export this patient already has, if any — a running one, or the most recent completed
   * one still inside its download window. Null when there is nothing to reattach to.
   */
  getCurrentPatientFileExport(patientId: string): Observable<PatientFileExportStatus | null> {
    const token = localStorage.getItem('token');
    return this.http
      .get<{ success: boolean; data: PatientFileExportStatus | null }>(
        `${this.apiUrl}/patients/${patientId}/export/current`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .pipe(map(res => res.data));
  }

  /**
   * Queue a full-file export. `options` is `{ sections: { <key>: boolean }, includeAudio }`;
   * omitting it exports everything except audio, which is what this endpoint did before the
   * options existed. The server re-validates the selection and refuses `includeAudio` outright
   * for a user without permission to download recordings.
   */
  startPatientFileExport(
    patientId: string,
    options?: { sections: { [key: string]: boolean }; includeAudio: boolean }
  ): Observable<{ exportId: string; status: string; reused?: boolean; options?: any }> {
    const token = localStorage.getItem('token');
    return this.http
      .post<{ success: boolean; data: { exportId: string; status: string } }>(
        `${this.apiUrl}/patients/${patientId}/export`,
        options ? { options } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .pipe(map(res => res.data));
  }

  getPatientFileExportStatus(patientId: string, exportId: string): Observable<PatientFileExportStatus> {
    const token = localStorage.getItem('token');
    return this.http
      .get<{ success: boolean; data: PatientFileExportStatus }>(
        `${this.apiUrl}/patients/${patientId}/export/${exportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .pipe(map(res => res.data));
  }

  /** Short-lived (15 min) direct link to the finished zip. Minted per call, never stored. */
  getPatientFileExportDownloadUrl(patientId: string, exportId: string): Observable<{ url: string; fileName: string; fileSize: number }> {
    const token = localStorage.getItem('token');
    return this.http
      .get<{ success: boolean; data: { url: string; fileName: string; fileSize: number } }>(
        `${this.apiUrl}/patients/${patientId}/export/${exportId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .pipe(map(res => res.data));
  }

}